import React from 'react';
import type { ElementType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatLocalizedDate, formatLocalizedDateTime } from '@/shared/utils/date';
import Text from '../Text';

/**
 * Props for the FormattedDate display component.
 */
export interface FormattedDateProps {
  /** ISO date string or Date object to format. */
  value: string | Date | null | undefined;
  /** Include time (HH:mm) alongside the date. */
  withTime?: boolean;
  /** Custom `Intl.DateTimeFormatOptions` for advanced formatting. */
  options?: Intl.DateTimeFormatOptions;
  /** Rendered when value is empty/invalid (defaults to null). */
  fallback?: ReactNode;
  /** HTML element to render as (defaults to `span`). */
  component?: ElementType;
  /** Additional CSS class names. */
  className?: string;
  /** Text size token (forwarded to `<Text>`). */
  size?: string;
  /** Text color token (forwarded to `<Text>`). */
  c?: string;
}

/**
 * Renders a locale-aware formatted date using the active i18n language.
 * Internally delegates to `formatLocalizedDate` / `formatLocalizedDateTime`
 * and renders through the `<Text>` DS component (no raw `<span>`).
 *
 * @param {FormattedDateProps} props - Component props.
 * @returns {React.ReactElement | null} Formatted date or fallback.
 */
export function FormattedDate({
  value,
  withTime,
  options,
  fallback = null,
  component = 'span',
  className,
  size,
  c,
}: FormattedDateProps): React.ReactElement | null {
  const { i18n } = useTranslation();
  const language = i18n.language;

  let formatted: string;
  if (options) {
    formatted = formatLocalizedDate(value, language, options);
  } else if (withTime) {
    formatted = formatLocalizedDateTime(value, language);
  } else {
    formatted = formatLocalizedDate(value, language);
  }

  if (!formatted) {
    return fallback as React.ReactElement | null;
  }

  return (
    <Text component={component} size={size} c={c} className={className}>
      {formatted}
    </Text>
  );
}
